/*
 * File: useStudentSectionData.ts
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import { useMemo } from 'react';

import type { CoursePeriod } from 'api/hooks/private/periods/GetCoursePeriod';
import { useGetStudent } from 'api/hooks/private/users/students/GetStudent';
import { useGetStudentLedger } from 'api/hooks/private/users/students/GetStudentLedger';
import { useGetStudentPeriodEnrollments } from 'api/hooks/private/users/students/GetStudentPeriods';

import { getDriveEnrollmentLabel } from 'pages/Admin/LegacyCalendar/util/getDriveEnrollmentLabel';

import { useDriveOverviewDialogContext } from '../../context/DriveOverviewDialogContext';
import { getDriveHistoryStats } from '../DriveHistory/getDriveHistorySummary';

interface UseStudentSectionDataParams {
  enrollment: CoursePeriod['period_enrollments'][0] | undefined;
}

/**
 * Custom hook that encapsulates all data fetching and transformation logic
 * for the StudentSection component
 */
export function useStudentSectionData({ enrollment }: UseStudentSectionDataParams) {
  const { period } = useDriveOverviewDialogContext();
  const courseId = period?.course_section.course_id ?? '';

  // Fetch detailed student information
  const studentQuery = useGetStudent({
    variables: {
      id: enrollment?.student_user_id ?? '',
    },
    enabled: !!enrollment,
  });

  // Fetch student period enrollments for drive history
  const studentPeriods = useGetStudentPeriodEnrollments({
    variables: {
      id: enrollment?.student_user_id ?? '',
      courseId,
    },
    enabled: !!enrollment,
  });

  // Fetch student ledger for balance information
  const studentLedger = useGetStudentLedger({
    variables: {
      id: enrollment?.student_user_id ?? '',
      showVoided: false,
    },
    enabled: !!enrollment,
  });

  // Memoized computed values
  const studentUser = studentQuery.data;
  const parents = studentQuery.data?.student.parents ?? [];

  const allPeriodEnrollments = useMemo(() => studentPeriods.data ?? [], [studentPeriods.data]);

  const driveLabel = enrollment ? getDriveEnrollmentLabel(enrollment, '#') : '';

  const driveHistoryStats = useMemo(() => {
    return getDriveHistoryStats(allPeriodEnrollments);
  }, [allPeriodEnrollments]);

  const balanceStatus = useMemo(() => {
    const ledgerData = studentLedger.data;
    if (!ledgerData || ledgerData.length === 0) {
      return { amount: 0, isOverdue: false };
    }

    // Get the last entry's balance (running total)
    const lastEntry = ledgerData[ledgerData.length - 1];
    const balance = Number(lastEntry.balance ?? 0);

    return {
      amount: balance,
      isOverdue: balance > 0, // Positive balance means money owed
    };
  }, [studentLedger.data]);

  return {
    studentUser,
    parents,
    driveLabel,
    allPeriodEnrollments,
    driveHistoryStats,
    balanceStatus,
    isLoading: studentQuery.isLoading || studentPeriods.isLoading || studentLedger.isLoading,
    isError: studentQuery.isError || studentPeriods.isError,
  };
}
